import Vue from 'vue'
import { apiRequest, normalizeApiError } from './api'

export const authState = Vue.observable({
  user: null,
  checked: false,
  sessionError: ''
})

let nativeFetch = null
let csrfToken = ''
let csrfPromise = null

function isApiRequest(input) {
  const value = typeof input === 'string' ? input : input && input.url
  if (!value) return false
  const url = new URL(value, window.location.origin)
  return url.origin === window.location.origin && url.pathname.startsWith('/api/')
}

function isUnsafe(method) {
  return !['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes((method || 'GET').toUpperCase())
}

async function refreshCsrfToken() {
  let lastError = null
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), 15000)
    try {
      const response = await nativeFetch('/api/auth/csrf', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      })
      if (!response.ok) {
        const error = new Error(response.status >= 500 ? '未连接到服务器，请确认后端服务已启动' : '服务器连接失败')
        error.status = response.status
        error.retryable = response.status >= 500
        throw error
      }
      const result = await response.json().catch(() => ({}))
      if (!result.token) {
        const error = new Error('安全服务返回的数据不完整')
        error.code = 'INVALID_CSRF_RESPONSE'
        error.retryable = true
        throw error
      }
      csrfToken = result.token
      return csrfToken
    } catch (error) {
      if (error.name === 'AbortError') error.timeoutMessage = '连接服务器超时，请确认后端服务已启动'
      lastError = normalizeApiError(error, '未连接到服务器，请确认后端服务已启动')
      if (!lastError.retryable || attempt >= 2) throw lastError
    } finally {
      window.clearTimeout(timer)
    }
    await new Promise(resolve => window.setTimeout(resolve, 800 * (attempt + 1)))
  }
  throw lastError || normalizeApiError(null, '未连接到服务器，请确认后端服务已启动')
}

async function ensureCsrfToken() {
  if (csrfToken) return csrfToken
  if (!csrfPromise) {
    csrfPromise = refreshCsrfToken().finally(() => {
      csrfPromise = null
    })
  }
  return csrfPromise
}

export function installSecureFetch() {
  if (nativeFetch || typeof window === 'undefined') return
  nativeFetch = window.fetch.bind(window)
  window.fetch = async (input, init = {}) => {
    if (!isApiRequest(input)) return nativeFetch(input, init)

    const options = { ...init, credentials: 'same-origin' }
    const headers = new Headers(init.headers || {})
    if (!headers.has('Accept')) headers.set('Accept', 'application/json')
    if (isUnsafe(options.method)) headers.set('X-XSRF-TOKEN', await ensureCsrfToken())
    options.headers = headers

    let response = await nativeFetch(input, options)
    if (response.status === 403 && isUnsafe(options.method)) {
      csrfToken = ''
      headers.set('X-XSRF-TOKEN', await ensureCsrfToken())
      response = await nativeFetch(input, { ...options, headers })
    }

    const value = typeof input === 'string' ? input : input.url
    const url = new URL(value, window.location.origin)
    if (response.status === 401 && !url.pathname.startsWith('/api/auth/')) {
      authState.user = null
      authState.checked = true
      window.dispatchEvent(new CustomEvent('auth-expired'))
    }
    return response
  }
}

export async function checkSession(force = false) {
  if (authState.checked && !force) return Boolean(authState.user)
  const previousUser = authState.user
  try {
    // During local startup the Vue server can become available a few seconds
    // before Spring Boot. Keep retrying the read-only session check long enough
    // for the backend to finish booting instead of leaving the whole page in a
    // stale disconnected state.
    const result = await apiRequest('/api/auth/session', {
      timeoutMs: 15000,
      retries: 6,
      retryDelayMs: 1000
    })
    authState.user = result.user || null
    authState.sessionError = ''
    return Boolean(authState.user)
  } catch (error) {
    if (error.status === 401 || error.status === 403) {
      authState.user = null
      authState.sessionError = ''
      return false
    }
    authState.user = previousUser
    authState.sessionError = previousUser
      ? ''
      : '无法连接后端服务，请确认后端已启动后刷新页面'
    return Boolean(previousUser)
  } finally {
    authState.checked = true
  }
}

export async function login(username, password) {
  const body = new URLSearchParams()
  body.set('username', username)
  body.set('password', password)
  const result = await apiRequest('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body,
    timeoutMs: 20000,
    timeoutMessage: '登录请求超时，请检查网络后重试'
  })
  if (!result.user) {
    const error = new Error('登录成功，但服务器未返回用户信息')
    error.suggestion = '请刷新页面重新登录；若仍出现，请联系管理员。'
    error.retryable = true
    throw error
  }
  authState.user = result.user
  authState.checked = true
  authState.sessionError = ''
  return result.user
}

export async function loginWithImpersonationTicket(ticket) {
  const result = await apiRequest('/api/auth/impersonation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json;charset=UTF-8' },
    body: JSON.stringify({ ticket }),
    timeoutMs: 15000,
    timeoutMessage: '进入用户前台超时，请返回后台重试'
  })
  if (!result.user) throw new Error('服务器未返回用户信息')
  authState.user = result.user
  authState.checked = true
  authState.sessionError = ''
  return result.user
}

export async function register(username, password, confirmPassword, displayName, referralCode = '') {
  await apiRequest('/api/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json;charset=UTF-8' },
    body: JSON.stringify({ username, password, confirmPassword, displayName, referralCode: referralCode || null }),
    timeoutMs: 20000,
    timeoutMessage: '注册请求超时，请检查网络后重试'
  })
  return login(username, password)
}

export async function logout() {
  let remoteLogout = true
  let logoutError = null
  try {
    await apiRequest('/api/auth/logout', {
      method: 'POST',
      timeoutMs: 15000,
      timeoutMessage: '退出请求超时'
    })
  } catch (error) {
    remoteLogout = false
    logoutError = error
  } finally {
    authState.user = null
    authState.checked = true
    authState.sessionError = ''
    csrfToken = ''
  }
  return { remoteLogout, error: logoutError }
}
/**
 * 登录态管理：保存当前用户、token 和 XSRF 信息，并在登录失效时通知应用层。
 * API 请求不应自行读取 localStorage，统一通过这里获取认证状态。
 */
